/**
 * home-photo-wall.js - the lightbox for Real Moments.
 *
 * The wall itself is server rendered and scrolls without any of this. The only
 * job here is opening a photo with the review it belongs to and the product it
 * is about, which is the one part that cannot be done in Liquid alone.
 *
 * Every value comes off the tile's own data attributes, written by Liquid from
 * the shop metafield, so the script never fetches and never invents: if a
 * field is absent the corresponding element is emptied rather than guessed at.
 */
(() => {
  'use strict';

  function init(root = document) {
    const wall = root.querySelector('[data-home-wall]');
    const lb = document.querySelector('[data-hpw-lb]');
    if (!wall || !lb || wall.dataset.hpwReady) return;
    wall.dataset.hpwReady = '1';

    const el = (sel) => lb.querySelector(sel);
    const img = el('[data-hpw-lb-img]');
    const stars = el('[data-hpw-lb-stars]');
    const who = el('[data-hpw-lb-a]');
    const vb = el('[data-hpw-lb-vb]');
    const title = el('[data-hpw-lb-ti]');
    const body = el('[data-hpw-lb-b]');
    const date = el('[data-hpw-lb-d]');
    const prod = el('[data-hpw-lb-prod]');
    const prodImg = el('[data-hpw-lb-pi]');
    const prodName = el('[data-hpw-lb-pt]');

    let opener = null;
    let paintToken = 0;

    const endpoint = (wall.dataset.hpwEndpoint || '').replace(/\/+$/, '');
    let grandTotal = Number(wall.dataset.hpwTotal) || 0;
    let fetched = wall.querySelectorAll('[data-hpw-open]').length;
    let fetching = false;

    // Every tile in the wall, in reading order, so the lightbox arrows walk
    // the whole set rather than stopping at the end of the row clicked.
    let tiles = [...wall.querySelectorAll('[data-hpw-open]')];
    let at = -1;

    const prevBtn = lb.querySelector('[data-hpw-prev]');
    const nextBtn = lb.querySelector('[data-hpw-next]');

    const fmt = (iso) => {
      if (!iso) return '';
      const d = new Date(`${iso}T00:00:00Z`);
      return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString(undefined,
        { year: 'numeric', month: 'short', day: 'numeric', timeZone: 'UTC' });
    };

    /**
     * Reveal the next batch in a row, fetching more when the row runs dry.
     *
     * The metafield carries the first tiles and the app serves the rest, in
     * the same order, so the wall keeps going until there are genuinely no
     * more photos rather than stopping at whatever fitted in a metafield.
     *
     * Fetched tiles are appended with the same rule the server applies when it
     * builds the wall: a customer never sits next to themselves. A tile that
     * would break it waits for the following slot instead of being dropped.
     */
    async function revealMore(btn) {
      const track = btn.closest('.hpw__track');
      const item = btn.closest('[data-hpw-more-item]');
      const step = Number(btn.dataset.step) || 12;

      let hiddenItems = [...track.querySelectorAll('.hpw__item[hidden]')];

      if (hiddenItems.length < step && endpoint && fetched < grandTotal && !fetching) {
        fetching = true;
        btn.disabled = true;
        try {
          const res = await fetch(`${endpoint}/photos/all?limit=${step * 2}&offset=${fetched}`,
            { headers: { Accept: 'application/json' } });
          if (res.ok) {
            const data = await res.json();
            if (typeof data.total === 'number') grandTotal = data.total;
            const incoming = Array.isArray(data.photos) ? data.photos : [];
            fetched += incoming.length;
            if (!incoming.length) grandTotal = fetched;   // nothing more to ask for
            appendTiles(track, incoming);
            hiddenItems = [...track.querySelectorAll('.hpw__item[hidden]')];
          } else {
            grandTotal = fetched;                         // stop asking
          }
        } catch (err) {
          grandTotal = fetched;
          console.error('[reviews] could not load more photos', err);
        } finally {
          fetching = false;
          btn.disabled = false;
        }
      }

      for (const el of hiddenItems.slice(0, step)) el.hidden = false;
      const left = Math.max(0, hiddenItems.length - step) + Math.max(0, grandTotal - fetched);
      const count = btn.querySelector('[data-hpw-more-count]');
      if (count) count.textContent = left;
      if (left <= 0 && item) item.hidden = true;
      syncRowArrows();
      refreshTiles();
    }

    /** Build tiles from fetched rows, keeping a customer off their own heels. */
    function appendTiles(track, rows) {
      const moreItem = track.querySelector('[data-hpw-more-item]');
      const key = (n) => String(n || '').trim().toLowerCase();
      const existing = [...track.querySelectorAll('[data-hpw-open]')];
      let lastName = existing.length ? key(existing[existing.length - 1].dataset.a) : null;

      const queue = [...rows];
      while (queue.length) {
        let i = queue.findIndex((t) => key(t.a) !== lastName);
        if (i === -1) i = 0;                       // nothing else available
        const t = queue.splice(i, 1)[0];
        const li = document.createElement('li');
        li.className = 'hpw__item';
        li.hidden = true;
        li.innerHTML = tileHTML(t);
        track.insertBefore(li, moreItem);
        lastName = key(t.a);
      }
    }

    function tileHTML(t) {
      const alt = `Customer photo of ${t.pt || 'a Daftboy product'}${t.a ? ` by ${t.a}` : ''}`;
      return `<button class="hpw__tile" type="button" data-hpw-open
        data-u="${esc(t.u)}" data-pt="${esc(t.pt)}" data-ph="${esc(t.ph)}"
        data-pi="${esc(t.pi || '')}" data-a="${esc(t.a)}" data-r="${esc(t.r)}"
        data-d="${esc(t.d || '')}" data-vb="${t.vb ? '1' : ''}"
        data-ti="${esc(t.ti || '')}" data-b="${esc(t.b)}${t.cut ? '&hellip;' : ''}"
        aria-label="Read ${esc(t.a)}'s review of ${esc(t.pt)}">
        <img class="hpw__img" src="${esc(t.t || t.u)}" width="240" height="240"
             loading="lazy" decoding="async" alt="${esc(alt)}"></button>`;
    }

    const esc = (v) => String(v ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;')
      .replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');

    /** The lightbox steps through whatever is on the page now, fetched or not. */
    function refreshTiles() {
      tiles = [...wall.querySelectorAll('[data-hpw-open]')];
    }

    function openAt(i) {
      if (i < 0 || i >= tiles.length) return;
      at = i;
      paint(tiles[i]);
      if (prevBtn) prevBtn.disabled = at <= 0;
      if (nextBtn) nextBtn.disabled = at >= tiles.length - 1;

      // Warm the neighbours. Someone looking at one photo usually looks at the
      // next one, and fetching it now means the swap has already happened by
      // the time they press the arrow.
      for (const n of [i + 1, i - 1]) {
        const t = tiles[n];
        if (!t || !t.dataset.u) continue;
        const pre = new Image();
        pre.src = t.dataset.u;
      }

      // Mark the strip and bring the active thumbnail into view, so stepping
      // with the arrows does not leave the marker somewhere off screen.
      const thumbs = lb.querySelectorAll('[data-hpw-pick]');
      thumbs.forEach((t, j) => t.setAttribute('aria-current', j === at ? 'true' : 'false'));
      const active = thumbs[at];
      if (active && active.scrollIntoView) {
        active.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      }
    }

    function paint(btn) {
      const d = btn.dataset;
      opener = btn;

      /**
       * Show the thumbnail at once, then swap in the original when it lands.
       *
       * Setting src straight to the original left the PREVIOUS photo on screen
       * until the new one finished downloading, so the text changed instantly
       * and the picture followed seconds later. These originals are what the
       * customer uploaded: the largest in the current wall is 2.4MB and takes
       * over five seconds, against 26KB for its thumbnail.
       *
       * The thumbnail is already in cache, because it is the tile that was
       * just clicked, so it paints immediately and the card never shows the
       * wrong photo.
       */
      const tileImg = btn.querySelector('.hpw__img');
      const thumb = tileImg ? (tileImg.currentSrc || tileImg.src) : '';
      img.alt = `Customer photo of ${d.pt || 'the product'}${d.a ? ` by ${d.a}` : ''}`;
      if (thumb) img.src = thumb;

      // A token per paint. Clicking next twice quickly must not let the first
      // original arrive late and overwrite the second photo.
      const token = ++paintToken;
      if (d.u && d.u !== thumb) {
        const full = new Image();
        full.onload = () => { if (token === paintToken) img.src = d.u; };
        full.src = d.u;
      }
      stars.style.setProperty('--star-rating', d.r || 5);
      stars.setAttribute('aria-label', `${d.r || 5} out of 5 stars`);
      who.textContent = d.a || '';
      vb.textContent = d.vb ? 'Verified buyer' : '';
      title.textContent = d.ti || '';
      body.textContent = d.b || '';
      date.textContent = fmt(d.d);

      // The hero product. Hidden outright when we have no image for it rather
      // than showing an empty frame.
      if (d.ph) {
        prod.href = `/products/${d.ph}`;
        prodName.textContent = d.pt || '';
        if (d.pi) { prodImg.src = d.pi; prodImg.alt = ''; prodImg.hidden = false; }
        else { prodImg.removeAttribute('src'); prodImg.hidden = true; }
        prod.hidden = false;
      } else {
        prod.hidden = true;
      }

      if (lb.hidden) {
        lb.hidden = false;
        document.body.classList.add('overflow-hidden');
        const x = lb.querySelector('[data-hpw-close]');
        if (x) x.focus({ preventScroll: true });
      }
    }

    function close() {
      lb.hidden = true;
      document.body.classList.remove('overflow-hidden');
      // Release the image so a large photo is not held in memory behind a
      // closed dialog.
      img.removeAttribute('src');
      if (opener && document.contains(opener)) opener.focus({ preventScroll: true });
      opener = null;
    }

    wall.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-hpw-open]');
      if (btn) openAt(tiles.indexOf(btn));

      // Row arrows. One viewport of tiles per press, which is what a reader
      // expects from an arrow and what a trackpad flick already does.
      // Load more: reveal the next batch in this row. Everything is already
      // in the HTML, so there is no request and nothing to fail.
      const more = e.target.closest('[data-hpw-more]');
      if (more) { revealMore(more); return; }

      const arrow = e.target.closest('[data-hpw-scroll]');
      if (arrow) {
        const track = arrow.parentElement.querySelector('[data-hpw-track]');
        if (track) track.scrollBy({ left: track.clientWidth * 0.9 * Number(arrow.dataset.hpwScroll), behavior: 'smooth' });
      }
    });

    if (prevBtn) prevBtn.addEventListener('click', () => openAt(at - 1));
    if (nextBtn) nextBtn.addEventListener('click', () => openAt(at + 1));

    /**
     * Show a row's arrows only once it actually overflows, and grey each one
     * out at the end it cannot move past. A control that cannot do anything is
     * worse than no control.
     */
    function syncRowArrows() {
      for (const wrap of wall.querySelectorAll('.hpw__rowwrap')) {
        const track = wrap.querySelector('[data-hpw-track]');
        const prev = wrap.querySelector('.hpw__nav--prev');
        const next = wrap.querySelector('.hpw__nav--next');
        if (!track || !prev || !next) continue;
        const overflows = track.scrollWidth > track.clientWidth + 4;
        prev.hidden = !overflows;
        next.hidden = !overflows;
        prev.disabled = track.scrollLeft <= 2;
        next.disabled = track.scrollLeft >= track.scrollWidth - track.clientWidth - 2;
      }
    }
    for (const track of wall.querySelectorAll('[data-hpw-track]')) {
      track.addEventListener('scroll', syncRowArrows, { passive: true });
    }
    let rt;
    window.addEventListener('resize', () => { clearTimeout(rt); rt = setTimeout(syncRowArrows, 120); });
    syncRowArrows();
    lb.addEventListener('click', (e) => {
      if (e.target.closest('[data-hpw-close]')) { close(); return; }
      // The previous and next buttons now sit inside the media box, so the
      // backdrop test below already spares them. Kept as an explicit guard:
      // when they were siblings of the panel, "not inside the panel" counted
      // them as the backdrop and every press closed the card.
      const pick = e.target.closest('[data-hpw-pick]');
      if (pick) { openAt(Number(pick.dataset.hpwPick)); return; }
      if (e.target.closest('[data-hpw-prev], [data-hpw-next]')) return;
      // Anything else outside the panel is the backdrop.
      if (!e.target.closest('.hpw-lb__panel')) close();
    });
    // Capture phase: the theme has its own key handling and the arrow keys
    // never reached a listener on document while the dialog was open.
    document.addEventListener('keydown', (e) => {
      if (lb.hidden) return;
      if (e.key === 'Escape') { e.preventDefault(); close(); }
      else if (e.key === 'ArrowLeft') { e.preventDefault(); openAt(at - 1); }
      else if (e.key === 'ArrowRight') { e.preventDefault(); openAt(at + 1); }
    }, true);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();
  document.addEventListener('shopify:section:load', (e) => init(e.target));
})();
